import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Users,
  Calendar,
  Award,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  ArrowRight,
  RefreshCw,
  UserCheck,
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';

const clampPercentage = (value) => Math.min(100, Math.max(0, Math.round(value || 0)));

const getCourseStatusMeta = (courseSummary) => {
  const completionRate = courseSummary?.completionRate || 0;
  const enrolled = courseSummary?.enrolled || 0;
  const overdueCount = courseSummary?.overdue || 0;

  if (overdueCount > 0) {
    return { label: 'Overdue', color: 'text-red-600' };
  }

  if (enrolled === 0) {
    return { label: 'No Learners Enrolled', color: 'text-gray-500' };
  }

  if (completionRate >= 100) {
    return { label: 'Completed', color: 'text-green-600' };
  }

  if (completionRate > 0) {
    return { label: 'In Progress', color: 'text-blue-600' };
  }

  return { label: 'Not Started', color: 'text-gray-500' };
};

const formatCategoryLabel = (category) => {
  if (!category) return 'Uncategorized';
  const normalized = String(category)
    .replace(/[_\s]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!normalized) return 'Uncategorized';
  return normalized
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const calculateAssigneeProgress = (assignee) => {
  if (!assignee) return 0;

  const completedLessons = typeof assignee.progress?.completedLessons === 'number'
    ? assignee.progress.completedLessons
    : null;
  const totalLessons = typeof assignee.progress?.totalLessons === 'number'
    ? assignee.progress.totalLessons
    : null;

  if (completedLessons !== null && totalLessons !== null && totalLessons > 0) {
    const progress = (Math.min(completedLessons, totalLessons) / totalLessons) * 100;
    return clampPercentage(progress);
  }

  if (typeof assignee.progress?.percentage === 'number') {
    return clampPercentage(assignee.progress.percentage);
  }

  if (typeof assignee.progress === 'number') {
    return clampPercentage(assignee.progress);
  }

  return 0;
};

const getDaysUntilDue = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = due - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const calculateAssigneeStatus = (assignee, assignmentDueDate) => {
  if (!assignee) return 'assigned';

  const progressValue = calculateAssigneeProgress(assignee);
  const progressStatus = assignee.progress?.status;
  const baseStatus = assignee.status;

  const isCompletedStatus = ['completed', 'passed', 'failed'].includes(progressStatus);
  const hasCompleted = progressValue >= 100 || baseStatus === 'completed' || baseStatus === 'passed' || baseStatus === 'failed' || isCompletedStatus;

  if (hasCompleted) {
    return 'completed';
  }

  const dueDate = assignmentDueDate ? new Date(assignmentDueDate) : null;
  const today = new Date();
  const isOverdue = dueDate && dueDate < today;

  if (isOverdue) {
    return 'overdue';
  }

  const hasProgress = progressValue > 0 || baseStatus === 'in_progress' || ['in_progress', 'started'].includes(progressStatus);
  if (hasProgress) {
    return 'in_progress';
  }

  return 'assigned';
};

const calculateAverageProgress = (assignment) => {
  if (!assignment) return 0;

  const assignees = Array.isArray(assignment.assignees) ? assignment.assignees : [];

  if (assignees.length > 0) {
    let totalCompletedLessons = 0;
    let totalLessonsAcrossAssignees = 0;
    const fallbackPercentages = [];

    assignees.forEach((assignee) => {
      const completedLessons = typeof assignee.progress?.completedLessons === 'number'
        ? assignee.progress.completedLessons
        : null;
      const totalLessons = typeof assignee.progress?.totalLessons === 'number'
        ? assignee.progress.totalLessons
        : null;

      if (completedLessons !== null && totalLessons !== null && totalLessons > 0) {
        totalCompletedLessons += Math.min(completedLessons, totalLessons);
        totalLessonsAcrossAssignees += totalLessons;
      } else {
        const percentage = calculateAssigneeProgress(assignee);
        fallbackPercentages.push(percentage);
      }
    });

    if (totalLessonsAcrossAssignees > 0) {
      const lessonBasedProgress = (totalCompletedLessons / totalLessonsAcrossAssignees) * 100;
      return clampPercentage(lessonBasedProgress);
    }

    if (fallbackPercentages.length > 0 && fallbackPercentages.length === assignees.length) {
      const percentageAvg = fallbackPercentages.reduce((sum, value) => sum + value, 0) / fallbackPercentages.length;
      return clampPercentage(percentageAvg);
    }
  }

  if (typeof assignment.progress === 'number') {
    return clampPercentage(assignment.progress);
  }

  return 0;
};

const calculateAssignmentStatus = (assignment) => {
  if (!assignment) return 'assigned';

  const assignees = Array.isArray(assignment.assignees) ? assignment.assignees : [];
  const statuses = assignees
    .map(assignee => calculateAssigneeStatus(assignee, assignment.dueDate))
    .filter(Boolean);
  const averageProgress = calculateAverageProgress(assignment);

  const allCompletedByStatus = statuses.length > 0 && statuses.every(s => s === 'completed');
  const fullyCompleted = averageProgress >= 100 || allCompletedByStatus;

  const daysUntilDue = getDaysUntilDue(assignment.dueDate);
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
  const hasOverdueStatus = statuses.some(s => s === 'overdue');
  const hasMeaningfulProgress = averageProgress > 0 || statuses.some(s => ['in_progress', 'completed'].includes(s));

  if (fullyCompleted) {
    return 'completed';
  } else if (isOverdue || hasOverdueStatus) {
    return 'overdue';
  } else if (hasMeaningfulProgress) {
    return 'in_progress';
  } else {
    return 'assigned';
  }
};

const calculateAssignmentStats = (assignments = []) => {
  const stats = {
    total: 0,
    completed: 0,
    inProgress: 0,
    overdue: 0,
    assigned: 0
  };

  assignments.forEach((assignment) => {
    const status = calculateAssignmentStatus(assignment);
    stats.total += 1;

    switch (status) {
      case 'completed':
        stats.completed += 1;
        break;
      case 'in_progress':
        stats.inProgress += 1;
        break;
      case 'overdue':
        stats.overdue += 1;
        break;
      default:
        stats.assigned += 1;
        break;
    }
  });

  stats.completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
  return stats;
};

const TrainingDashboard = () => {
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);

  // API Data State - initialize as null to prevent flashing
  const [kpiData, setKpiData] = useState({
    inProgress: 0,
    overdue: 0,
    completionRate: 0,
    upcomingSessions: 0,
    completedAssignments: 0,
    assigned: 0,
    totalAssignments: 0
  });
  const [topCourses, setTopCourses] = useState(null);
  const [expiringCertifications, setExpiringCertifications] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const isInitialMount = useRef(true);


  const buildTopCoursesFromAssignments = useCallback((assignments = []) => {
    if (!Array.isArray(assignments) || assignments.length === 0) {
      setTopCourses([]);
      return;
    }

    const courseSummaryMap = new Map();

    assignments.forEach((assignment) => {
      const course = assignment?.course;
      if (!course) return;

      const courseId = course._id || course.id || course.courseId;
      if (!courseId) return;

      if (!courseSummaryMap.has(courseId)) {
        courseSummaryMap.set(courseId, {
          id: courseId,
          title: course.title || 'Untitled Course',
          category: course.category || 'Uncategorized',
          enrolled: 0,
          completed: 0,
          inProgress: 0,
          overdue: 0
        });
      }

      const summary = courseSummaryMap.get(courseId);
      const assignees = Array.isArray(assignment.assignees) ? assignment.assignees : [];

      assignees.forEach((assignee) => {
        summary.enrolled += 1;
        const status = calculateAssigneeStatus(assignee, assignment.dueDate);
        if (status === 'completed') {
          summary.completed += 1;
        } else if (status === 'in_progress') {
          summary.inProgress += 1;
        } else if (status === 'overdue') {
          summary.overdue += 1;
        }
      });
    });

    const topCourseList = Array.from(courseSummaryMap.values())
      .map((course) => ({
        ...course,
        completionRate: course.enrolled > 0
          ? Math.round((course.completed / course.enrolled) * 100)
          : 0
      }))
      .filter((course) => course.enrolled > 0)
      .sort((a, b) => (b.enrolled || 0) - (a.enrolled || 0))
      .slice(0, 3);

    setTopCourses(topCourseList);
  }, [setTopCourses]);

  // Load dashboard data on component mount only
  useEffect(() => {
    if (isInitialMount.current) {
      loadDashboardData();
      isInitialMount.current = false;
    }
  }, []);

  // Load data when filters change - removed to prevent infinite loops
  // Filter changes will be handled by manual refresh or form submissions

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);


  // Load dashboard data with comprehensive error handling
  const loadDashboardData = async () => {
    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load all dashboard data in parallel with individual error handling
      const results = await Promise.allSettled([
        loadAnalytics(),
        loadAssignmentStats(),
        loadExpiringCertifications()
      ]);

      // Check for any failed promises
      const failedResults = results.filter(result => result.status === 'rejected');
      if (failedResults.length > 0) {
        // Don't set error for partial failures, just log them
      }

    } catch (error) {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }

    // Safety timeout to ensure loading state is cleared
    setTimeout(() => {
      if (isMountedRef.current && loading) {
        setLoading(false);
      }
    }, 10000); // 10 second timeout
  };

  // Load dashboard data silently without showing loading state
  const loadDashboardDataSilently = async () => {
    setError(null);

    try {
      // Load all dashboard data in parallel with individual error handling
      const results = await Promise.allSettled([
        loadAnalytics(),
        loadAssignmentStats(),
        loadExpiringCertifications()
      ]);

      // Check for any failed promises
      const failedResults = results.filter(result => result.status === 'rejected');
      if (failedResults.length > 0) {
        // Don't set error for partial failures, just log them
      }

    } catch (error) {
      setError('Failed to load dashboard data. Please try again.');
    }
  };

  // Load analytics data with comprehensive error handling
  const loadAnalytics = async () => {
    try {
      // Use default 30 days period
      const backendPeriod = '30d';
      const response = await trainingService.getAnalytics(backendPeriod);

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from analytics API');
      }

      if ((response.success || response.responseCode === 200) && isMountedRef.current) {
        const data = response.data || {};

        // Safely set analytics with null checks
        setAnalytics(data);

        const rawUpcomingSessions = Array.isArray(data.upcomingSessions)
          ? data.upcomingSessions.length
          : Number(data.upcomingSessions);
        const fallbackUpcomingSessions = Number(data?.kpis?.sessionsThisWeek);
        const upcomingSessionsValue = Number.isFinite(rawUpcomingSessions) && rawUpcomingSessions >= 0
          ? rawUpcomingSessions
          : (Number.isFinite(fallbackUpcomingSessions) && fallbackUpcomingSessions >= 0
            ? fallbackUpcomingSessions
            : 0);

        setKpiData(prev => ({
          ...prev,
          upcomingSessions: Number.isFinite(upcomingSessionsValue) && upcomingSessionsValue >= 0
            ? upcomingSessionsValue
            : 0
        }));
      } else {
        if (isMountedRef.current) {
          setKpiData(prev => ({
            ...prev,
            upcomingSessions: 0
          }));
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setKpiData(prev => ({
          ...prev,
          upcomingSessions: 0
        }));
      }
    }
  };

  const loadAssignmentStats = async () => {
    try {
      const queryParams = {
        limit: 200,
        sortBy: 'assignedDate',
        sortOrder: 'desc'
      };

      const response = await trainingService.getAssignments(queryParams);

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from assignments API');
      }

      if ((response.success || response.responseCode === 200) && isMountedRef.current) {
        const assignmentsData = response.data?.assignments || response.data || [];
        const assignmentsArray = Array.isArray(assignmentsData) ? assignmentsData : [];
        // Only consider course assignments that actually have assignees
        const assignedCourses = assignmentsArray.filter((assignment) => {
          const hasAssignees = Array.isArray(assignment.assignees) && assignment.assignees.length > 0;
          if (!hasAssignees) {
            return false;
          }

          const contentType = (assignment.contentType || 'course').toLowerCase();
          const isCourseAssignment = !contentType || contentType === 'course';
          return isCourseAssignment;
        });
        const stats = calculateAssignmentStats(assignedCourses);

        setKpiData(prev => ({
          ...prev,
          inProgress: stats.inProgress,
          overdue: stats.overdue,
          completionRate: stats.completionRate,
          completedAssignments: stats.completed,
          assigned: stats.assigned,
          totalAssignments: stats.total
        }));

        if (assignedCourses.length > 0) {
          buildTopCoursesFromAssignments(assignedCourses);
        } else {
          setTopCourses([]);
        }
      } else {
        if (isMountedRef.current) {
          setKpiData(prev => ({
            ...prev,
            inProgress: 0,
            overdue: 0,
            completionRate: 0,
            completedAssignments: 0,
            assigned: 0,
            totalAssignments: 0
          }));
        }
        setTopCourses([]);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setKpiData(prev => ({
          ...prev,
          inProgress: 0,
          overdue: 0,
          completionRate: 0,
          completedAssignments: 0,
          assigned: 0,
          totalAssignments: 0
        }));
      }
      setTopCourses([]);
    }
  };

  // Load expiring certifications with comprehensive error handling
  const loadExpiringCertifications = async () => {
    try {
      const queryParams = {
        expiring: true,
        limit: 5
      };

      // Note: Backend API doesn't support department filtering yet
      // TODO: Add department filtering support in backend API

      const response = await trainingService.getCertificates(queryParams);

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from certificates API');
      }

      if ((response.success || response.responseCode === 200) && isMountedRef.current) {
        const certificates = response.data || [];

        // Validate that certificates is an array
        if (Array.isArray(certificates)) {
          // Filter out already expired certifications (negative days) - no date filtering
          const nonExpiredCertificates = certificates.filter(cert => {
            const daysToExpiry = cert.daysToExpiry || cert.daysLeft || 0;
            return daysToExpiry > 0; // Only show certifications that haven't expired yet
          });

          setExpiringCertifications(nonExpiredCertificates);
        } else {
          setExpiringCertifications([]);
        }
      } else {
        if (isMountedRef.current) {
          setExpiringCertifications([]);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setExpiringCertifications([]);
      }
    }
  };

  // Navigation handlers
  const handleNavigateTo = (path) => {
    navigate(path);
  };


  // Navigation handlers
  const handleNavigateToCatalog = () => {
    handleNavigateTo('/training_catalog');
  };

  const handleNavigateToAssignments = () => {
    handleNavigateTo('/assignments_enrollment');
  };

  const handleNavigateToSessions = () => {
    handleNavigateTo('/sessions_scheduling');
  };

  const handleNavigateToReports = () => {
    handleNavigateTo('/reports_analytics');
  };

  const handleNavigateToExpiringCertifications = () => {
    handleNavigateTo('/compliance_renewals?filter=expiring');
  };
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-agrosaf-green" />
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="training" action="read">
            <button
              onClick={loadDashboardData}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-green-600 transition-colors mx-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  // Check if data is loaded - prevent showing default values
  if (!topCourses || !expiringCertifications) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-agrosaf-green" />
        <span className="ml-2 text-gray-600">Loading dashboard data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learning & Development Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor and manage organizational learning initiatives</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{kpiData?.inProgress || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-gray-900">{kpiData?.overdue || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{kpiData?.completionRate || 0}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">{kpiData?.upcomingSessions || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleNavigateToCatalog}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="h-6 w-6 text-blue-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Learning Catalog</p>
                <p className="text-sm text-gray-600">Manage learning content</p>
              </div>
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleNavigateToAssignments}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <UserCheck className="h-6 w-6 text-green-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Learning Assignments</p>
                <p className="text-sm text-gray-600">Monitor learner progress</p>
              </div>
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleNavigateToSessions}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Calendar className="h-6 w-6 text-purple-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Class Schedules</p>
                <p className="text-sm text-gray-600">Manage class schedules</p>
              </div>
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleNavigateToReports}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BarChart3 className="h-6 w-6 text-orange-600 mr-3" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Learning Analytics</p>
                <p className="text-sm text-gray-600">View learning analytics</p>
              </div>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Courses */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Most Popular Content</h3>
              <button
                onClick={handleNavigateToAssignments}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                View Learning Assignments
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topCourses && topCourses.length > 0 ? topCourses.map((course) => (
                <div key={course.id || course._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 capitalize">{course.title || 'Untitled Course'}</h4>
                    <p className="text-sm text-gray-600">{formatCategoryLabel(course.category)}</p>
                    <div className="flex items-center mt-2 space-x-4">
                      <span className="text-sm text-gray-500">
                        <Users className="h-4 w-4 inline mr-1" />
                        {course.enrolled || 0} Learners Enrolled
                      </span>
                      <span className="text-sm text-gray-500">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        {course.completed || 0} Completions
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const statusMeta = getCourseStatusMeta(course);
                    return (
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${statusMeta.color}`}>{statusMeta.label}</div>
                        <div className="text-xs text-gray-500">{course.completionRate || 0}% completion</div>
                      </div>
                    );
                  })()}
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No learning content available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expiring Certifications */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Expiring Certifications</h3>
              <PermissionGate permission="training" action="read">
                <button
                  onClick={handleNavigateToExpiringCertifications}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </button>
              </PermissionGate>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {expiringCertifications && expiringCertifications.length > 0 ? expiringCertifications.map((cert) => {
                const employeeSource = cert.employeeName || cert.employee;
                const employeeDisplay = typeof employeeSource === 'object' && employeeSource !== null
                  ? employeeSource.name ||
                  `${employeeSource.firstName || ''} ${employeeSource.lastName || ''}`.trim() ||
                  employeeSource.email ||
                  employeeSource.id ||
                  'Unknown Employee'
                  : (employeeSource || 'Unknown Employee');
                const certificationSource = cert.certification || cert.course;
                const certificationDisplay = typeof certificationSource === 'object' && certificationSource !== null
                  ? certificationSource.name || certificationSource.title || 'Unknown Course'
                  : (certificationSource || 'Unknown Course');
                const departmentDisplay =
                  (cert?.employee?.department) ||
                  (typeof cert.department === 'object' && cert.department !== null
                    ? cert.department.name || cert.department.title
                    : cert.department) ||
                  cert?.metadata?.department ||
                  'Unknown Department';

                return (
                  <div key={cert.id || cert._id || employeeDisplay} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 capitalize">{employeeDisplay}</h4>
                      <p className="text-sm text-gray-600 capitalize">{certificationDisplay}</p>
                      <p className="text-sm text-gray-500 capitalize">{departmentDisplay}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${(cert.daysToExpiry || cert.daysLeft || 0) <= 15 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {cert.daysToExpiry || cert.daysLeft || 0} Days Remaining
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires On {cert.expiryDate ? formatDate(cert.expiryDate, { includeTime: false }) : 'Unknown'}
                      </div>
                    </div>
                  </div>
                )
              }) : (
                <div className="text-center py-8 text-gray-500">
                  <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No expiring certifications</p>
                  <p className="text-sm text-gray-400 mt-1">All certifications are up to date</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TrainingDashboard;


